import type { PublicNotificationDto } from '~~/shared/types/notification'

export function useNotifications() {
  const analytics = useAnalytics()
  const requestFetch = useRequestFetch()
  const items = useState<PublicNotificationDto[]>('morning-notifications', () => [])
  const unreadCount = useState('morning-notifications-unread', () => 0)
  const pending = useState('morning-notifications-pending', () => false)
  async function refresh(
    limit = 20,
    filter: 'all' | 'unread' | 'read' | 'archived' = 'all',
    offset = 0,
    category?: string,
  ) {
    pending.value = true
    try {
      const [list, count] = await Promise.all([
        requestFetch<{
          notifications: PublicNotificationDto[]
          total: number
          offset: number
          limit: number
        }>('/api/notifications', {
          query: { filter, limit, offset, category },
          cache: 'no-store',
        }),
        requestFetch<{ count: number }>('/api/notifications/unread-count', {
          cache: 'no-store',
        }),
      ])
      if (offset === 0) items.value = list.notifications
      else items.value.push(...list.notifications)
      unreadCount.value = count.count
      return list
    } finally {
      pending.value = false
    }
  }
  async function read(id: string, track = true) {
    const index = items.value.findIndex((item) => item.id === id)
    const wasUnread = index >= 0 && items.value[index]!.readAt === null
    try {
      const value = await requestFetch<PublicNotificationDto>(`/api/notifications/${id}/read`, {
        method: 'PATCH',
      })
      if (index >= 0) items.value[index] = value
      if (wasUnread) unreadCount.value = Math.max(0, unreadCount.value - 1)
      if (track) analytics.trackEvent('notification_action', { action: 'read' })
    } catch (cause) {
      if (track)
        analytics.trackEvent('operation_failure', {
          domain: 'notification',
          action: 'read',
          status_category: analyticsStatusCategory(cause),
        })
      throw cause
    }
  }
  async function unread(id: string) {
    const index = items.value.findIndex((item) => item.id === id)
    const wasRead = index >= 0 && items.value[index]!.readAt !== null
    try {
      const value = await requestFetch<PublicNotificationDto>(`/api/notifications/${id}/unread`, {
        method: 'PATCH',
      })
      if (index >= 0) items.value[index] = value
      if (wasRead) unreadCount.value += 1
      analytics.trackEvent('notification_action', { action: 'unread' })
    } catch (cause) {
      analytics.trackEvent('operation_failure', {
        domain: 'notification',
        action: 'unread',
        status_category: analyticsStatusCategory(cause),
      })
      throw cause
    }
  }
  async function readAll() {
    try {
      await requestFetch('/api/notifications/read-all', { method: 'POST' })
      items.value = items.value.map((item) => ({
        ...item,
        readAt: item.readAt ?? new Date().toISOString(),
      }))
      unreadCount.value = 0
      analytics.trackEvent('notification_action', { action: 'read_all' })
    } catch (cause) {
      analytics.trackEvent('operation_failure', {
        domain: 'notification',
        action: 'read_all',
        status_category: analyticsStatusCategory(cause),
      })
      throw cause
    }
  }
  async function archive(id: string) {
    const value = items.value.find((item) => item.id === id)
    try {
      await requestFetch(`/api/notifications/${id}/archive`, { method: 'PATCH' })
      items.value = items.value.filter((item) => item.id !== id)
      if (value?.readAt === null) unreadCount.value = Math.max(0, unreadCount.value - 1)
      analytics.trackEvent('notification_action', {
        action: 'archive',
        category: value?.category,
        has_action: Boolean(value?.actionUrl),
      })
    } catch (cause) {
      analytics.trackEvent('operation_failure', {
        domain: 'notification',
        action: 'archive',
        status_category: analyticsStatusCategory(cause),
      })
      throw cause
    }
  }
  async function remove(id: string) {
    const value = items.value.find((item) => item.id === id)
    try {
      await requestFetch(`/api/notifications/${id}`, { method: 'DELETE' })
      items.value = items.value.filter((item) => item.id !== id)
      if (value?.readAt === null && value.archivedAt === null)
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      analytics.trackEvent('notification_action', {
        action: 'delete',
        category: value?.category,
        has_action: Boolean(value?.actionUrl),
      })
    } catch (cause) {
      analytics.trackEvent('operation_failure', {
        domain: 'notification',
        action: 'delete',
        status_category: analyticsStatusCategory(cause),
      })
      throw cause
    }
  }
  function receive(value: PublicNotificationDto) {
    if (!items.value.some((item) => item.id === value.id)) {
      items.value.unshift(value)
      unreadCount.value += 1
    }
  }
  return { items, unreadCount, pending, refresh, read, unread, readAll, archive, remove, receive }
}
